import {
  closeCurrentDialog,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {head, isEmpty, isNil, isNotNil, splitEvery} from 'ramda';

import {
  useAssignSyncedMessageCustomerMutation,
  useAssignSyncedMessageRecordMutation,
  useMarkCommunicationsAsOpenedMutation,
  useMarkCommunicationsAsReadMutation,
  useMarkCommunicationsAsUnreadMutation,
  useUnassignSyncedMessageCustomerMutation,
  useUnassignSyncedMessageRecordMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  MAX_COMMUNICATION_IDS_PER_REQUEST,
  SentMessagePreviewDialog,
  catchUnhandledDataGridActions,
  entityResourceResolver,
  handleApiError,
  useIsCommunicationReadStateEnabled,
} from '@dms/shared';

import {
  ApiError,
  EMPTY_PLACEHOLDER,
  Nullish,
  suffixTestId,
  TestIdProps,
  valueOrPlaceholder,
} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {getSyncedMessagesGridConfig} from '../utils/getSyncedMessagesGridConfig';
import {AssignCustomerDialog} from './AssignCustomerDialog';
import {AssignRecordDialog} from './AssignRecordDialog';

type LinkedEntityColumnValue = {
  label?: string;
  // EagGrid LINK cells carry the target url directly
  url?: string;
  // legacy DataGrid entity cells carry the entity id (+ optionally its resource type)
  id?: string;
  resourceId?: string;
};

type SyncedMessagesDatagridProps = TestIdProps & {
  gridCode: 'messaging-user-synced-message' | 'messaging-tenant-synced-message';
};

export function SyncedMessagesDatagrid(props: SyncedMessagesDatagridProps) {
  const [assignCustomer] = useAssignSyncedMessageCustomerMutation();
  const [unassignCustomer] = useUnassignSyncedMessageCustomerMutation();
  const [assignRecord] = useAssignSyncedMessageRecordMutation();
  const [unassignRecord] = useUnassignSyncedMessageRecordMutation();
  const [markCommunicationsAsOpened] = useMarkCommunicationsAsOpenedMutation();
  const [markCommunicationsAsRead] = useMarkCommunicationsAsReadMutation();
  const [markCommunicationsAsUnread] = useMarkCommunicationsAsUnreadMutation();
  const isReadStateEnabled = useIsCommunicationReadStateEnabled();
  const gridConfig = getSyncedMessagesGridConfig(isReadStateEnabled);

  const actionCallback: ActionCallback<typeof gridConfig.predefinedColumns> = ({
    actionKey,
    rowId,
    rowData,
    getColumnData,
    refreshData,
  }) => {
    // Only mark-read / mark-unread run in bulk; every other action is per row.
    const syncedMessageId = Array.isArray(rowId) ? head(rowId) : rowId;
    // Bulk actions get no getColumnData, so the ids are read from the row data of every row.
    const communicationIds = getCommunicationIds(rowData);

    const changeReadState = (
      markCommunications: typeof markCommunicationsAsRead | typeof markCommunicationsAsUnread
    ) =>
      Promise.all(
        splitEvery(MAX_COMMUNICATION_IDS_PER_REQUEST, communicationIds).map((ids) =>
          markCommunications({communicationIds: ids}).unwrap().catch(handleApiError)
        )
      ).then(() => refreshData());

    // Returns the read state change, so the grid shows the bulk action as loading until it ends.
    return match(actionKey)
      .with('mark-read', () => changeReadState(markCommunicationsAsRead))
      .with('mark-unread', () => changeReadState(markCommunicationsAsUnread))
      .with('preview', () => {
        const content = getColumnData<string>('content')?.value;

        if (isNil(content) || isEmpty(content)) {
          showNotification.error();
          return;
        }

        const from = getColumnData<string[]>('from')?.value;
        const to = getColumnData<string[]>('to')?.value;
        const subject = getColumnData<string>('subject')?.value;
        const receivedAt = getColumnData<string>('receivedAt')?.value;
        const record = getColumnData<LinkedEntityColumnValue>('resource')?.value;
        const recordReasoning = getColumnData<string>('recordReasoning')?.value;
        const customer = getColumnData<LinkedEntityColumnValue>('customerId')?.value;
        const customerReasoning = getColumnData<string>('customerReasoning')?.value;

        // Previewing a message is seeing it; the backend reads it only when the user owns it.
        if (isReadStateEnabled && !isEmpty(communicationIds)) {
          markCommunicationsAsOpened({communicationIds})
            .unwrap()
            .then(() => refreshData())
            // Silent because nobody asked for this write and previewing again retries it.
            .catch((error: ApiError) => handleApiError(error, {silent: true}));
        }

        openDialog(
          <SentMessagePreviewDialog
            body={valueOrPlaceholder(content)}
            recipient={firstOrPlaceholder(to)}
            sender={firstOrPlaceholder(from)}
            sentAt={valueOrPlaceholder(receivedAt)}
            subject={subject && !isEmpty(subject) ? subject : EMPTY_PLACEHOLDER}
            record={{
              label: valueOrPlaceholder(record?.label),
              href: getEntityCellUrl(record),
            }}
            recordReasoning={valueOrPlaceholder(recordReasoning)}
            customer={{
              label: valueOrPlaceholder(customer?.label),
              href: getEntityCellUrl(customer, 'CUSTOMER'),
            }}
            customerReasoning={valueOrPlaceholder(customerReasoning)}
          />,
          {
            title: i18n.t('entity.email.labels.sentEmail'),
            size: 'xLarge',
            buttons: [
              {
                title: i18n.t('general.actions.close'),
                onClick: closeCurrentDialog,
                variant: 'secondary',
              },
            ],
          }
        );
      })
      .with('assign-customer', () => {
        if (isNil(syncedMessageId)) {
          return;
        }

        openDialog(
          <AssignCustomerDialog
            onConfirm={(customerId) =>
              assignCustomer({syncedMessageId, customerId})
                .unwrap()
                .then(() => {
                  refreshData();
                  closeCurrentDialog();
                })
                .catch(handleApiError)
            }
          />,
          {title: i18n.t('general.actions.assignCustomer')}
        );
      })
      .with('assign-record', () => {
        if (isNil(syncedMessageId)) {
          return;
        }

        openDialog(
          <AssignRecordDialog
            onConfirm={(recordId) =>
              assignRecord({syncedMessageId, recordId})
                .unwrap()
                .then(() => {
                  refreshData();
                  closeCurrentDialog();
                })
                .catch(handleApiError)
            }
          />,
          {title: i18n.t('general.actions.assignRecord'), size: 'large'}
        );
      })
      .with('unassign-customer', () => {
        if (isNil(syncedMessageId)) {
          return;
        }

        openConfirmDialog({
          text: i18n.t('entity.email.actions.unassignCustomerConfirm'),
          onConfirm: () =>
            unassignCustomer({syncedMessageId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .with('unassign-record', () => {
        if (isNil(syncedMessageId)) {
          return;
        }

        openConfirmDialog({
          text: i18n.t('entity.email.actions.unassignRecordConfirm'),
          onConfirm: () =>
            unassignRecord({syncedMessageId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => {
        // Block body: a returned string would be taken by the grid as a link to open.
        catchUnhandledDataGridActions(actionKey);
      });
  };

  return (
    <DmsDataGrid
      gridCode={props.gridCode}
      actionCallback={actionCallback}
      predefinedColumns={gridConfig.predefinedColumns}
      isRowHighlighted={gridConfig.isRowHighlighted}
      data-testid={suffixTestId('datagrid', props)}
    />
  );
}

function getCommunicationIds(rowData: Record<string, unknown> | Record<string, unknown>[]) {
  const rows = Array.isArray(rowData) ? rowData : [rowData];

  // A synced copy without a communication has a null id and cannot carry a read state.
  return rows.flatMap((row) =>
    match(row.communicationId)
      .with({value: Pattern.string}, (cell) => [cell.value])
      .otherwise(() => [])
  );
}

function firstOrPlaceholder(values: (string | undefined)[] | undefined): string {
  const first = values?.[0];
  return isNil(first) ? EMPTY_PLACEHOLDER : first;
}

// Mirrors how the grid itself links entity cells: EagGrid LINK cells already
// contain the url, legacy DataGrid cells resolve it from resourceId + id.
function getEntityCellUrl(
  cell: LinkedEntityColumnValue | Nullish,
  fallbackResourceId?: string
): string | undefined {
  return match(cell)
    .with(Pattern.nullish, () => undefined)
    .with({url: Pattern.string}, (linkCell) => linkCell.url)
    .with({id: Pattern.string}, (entityCell) => {
      const resourceId = entityCell.resourceId ?? fallbackResourceId;
      return isNotNil(resourceId) ? entityResourceResolver(resourceId, entityCell.id) : undefined;
    })
    .otherwise(() => undefined);
}
